import React, { useEffect, useState } from 'react';
import {
  FiPlus,
  FiRefreshCw,
  FiUser,
  FiZap,
  FiEdit3,
  FiMessageSquare,
  FiTrash2,
  FiFileText,
} from 'react-icons/fi';

import { getTaskHistory, getCompleteTaskHistory } from '@/services/taskHistory';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { Button, buttonVariants } from '@/components/Button';
import { Spinner } from '@/components/Spinner/Spinner.component';
import { EmptyState } from '@/components/EmptyState';
import { formatRelativeTime } from '../TaskDetail.utils';
import styles from './HistoryPanel.module.scss';

const CHANGE_META = {
  CREATE: { icon: <FiPlus size={13} />, tone: 'success' },
  STATUS_CHANGE: { icon: <FiRefreshCw size={13} />, tone: 'info' },
  ASSIGNEE_CHANGE: { icon: <FiUser size={13} />, tone: 'purple' },
  PRIORITY_CHANGE: { icon: <FiZap size={13} />, tone: 'orange' },
  UPDATE: { icon: <FiEdit3 size={13} />, tone: 'default' },
  COMMENT_ADDED: { icon: <FiMessageSquare size={13} />, tone: 'success' },
  COMMENT_EDITED: { icon: <FiEdit3 size={13} />, tone: 'orange' },
  COMMENT_DELETED: { icon: <FiTrash2 size={13} />, tone: 'error' },
};
const DEFAULT_CHANGE_META = { icon: <FiFileText size={13} />, tone: 'default' };

const formatChangeValue = (value) => {
  if (value === null || value === undefined) return 'None';
  if (typeof value === 'string' && value.trim() === '') return 'Empty';
  return String(value);
};

function ChangeDetails({ entry }) {
  if (entry.changeType === 'CREATE') {
    return (
      <Text variant={textVariantsEnum.Text_sm} color="gray-700">
        created this task
      </Text>
    );
  }
  return (
    <Text variant={textVariantsEnum.Text_sm} color="gray-700">
      changed <b>{entry.field}</b> from{' '}
      <span className={styles.oldValue}>
        {formatChangeValue(entry.previousValues?.[entry.field])}
      </span>{' '}
      to{' '}
      <span className={styles.newValue}>
        {formatChangeValue(entry.newValues?.[entry.field])}
      </span>
    </Text>
  );
}

export const HistoryPanel = ({ taskId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextOffset, setNextOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasOlderChunks, setHasOlderChunks] = useState(false);
  const [showingAll, setShowingAll] = useState(false);

  const fetchHistory = async (reset = true) => {
    if (reset) {
      setLoading(true);
      setError(null);
    } else {
      setLoadingMore(true);
    }
    try {
      const response = await getTaskHistory(taskId, 10, reset ? 0 : nextOffset);
      setHistory((prev) =>
        reset ? response.history || [] : [...prev, ...(response.history || [])]
      );
      setHasMore(Boolean(response.hasMore));
      setNextOffset(response.nextOffset || 0);
      setTotalCount(response.totalCount || 0);
      setHasOlderChunks(Boolean(response.hasOlderChunks));
    } catch (err) {
      setError('Failed to load task history');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!taskId) return;
    setShowingAll(false);
    fetchHistory(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const loadCompleteHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getCompleteTaskHistory(taskId);
      setHistory(response.history || []);
      setHasMore(false);
      setTotalCount(response.totalCount || 0);
      setHasOlderChunks(false);
      setShowingAll(true);
    } catch (err) {
      setError('Failed to load complete history');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.centered}>
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.centered}>
        <Text variant={textVariantsEnum.Text_sm} color="error-600">
          {error}
        </Text>
        <Button
          text="Try again"
          variant={buttonVariants.outline}
          onClick={() => fetchHistory(true)}
        />
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <EmptyState
        title="No activity yet"
        description="Activity will appear here as changes are made to this task."
      />
    );
  }

  return (
    <div className={styles.historyPanel}>
      <div className={styles.timeline}>
        {history.map((entry, index) => {
          const meta = CHANGE_META[entry.changeType] || DEFAULT_CHANGE_META;
          return (
            <div key={entry.id || index} className={styles.entry}>
              <span
                className={styles.entryIcon}
                data-tone={meta.tone}
                aria-hidden="true"
              >
                {meta.icon}
              </span>
              <div className={styles.entryBody}>
                <div className={styles.entryHeader}>
                  <Text
                    variant={textVariantsEnum.Text_sm}
                    fontWeight={fontWeightEnum.Semibold}
                  >
                    {entry.changedBy?.userName || 'Unknown User'}
                  </Text>
                  <Text variant={textVariantsEnum.Text_xs} color="gray-500">
                    {formatRelativeTime(entry.timestamp)}
                  </Text>
                </div>
                <ChangeDetails entry={entry} />
                {entry.commentText && (
                  <Text
                    variant={textVariantsEnum.Text_sm}
                    color="gray-600"
                    className={styles.commentText}
                  >
                    {entry.commentText}
                  </Text>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className={styles.footer}>
        <Text variant={textVariantsEnum.Text_xs} color="gray-500">
          {showingAll
            ? `Showing all ${history.length} activities`
            : `Showing ${history.length} of ${totalCount} activities`}
        </Text>
        <div className={styles.footerActions}>
          {hasMore && !showingAll && (
            <Button
              text={loadingMore ? 'Loading…' : 'Load more'}
              variant={buttonVariants.outline}
              disabled={loadingMore}
              onClick={() => fetchHistory(false)}
            />
          )}
          {hasOlderChunks && !showingAll && (
            <Button
              text="Show all history"
              variant={buttonVariants.ghost}
              onClick={loadCompleteHistory}
            />
          )}
        </div>
      </div>
    </div>
  );
};

HistoryPanel.displayName = 'HistoryPanel';
